import { useState, useCallback, useEffect } from "react";

export type RecentItemType = "condition" | "medication";

export interface RecentItem {
  type: RecentItemType;
  id: number;
  name: string;
  simpleName?: string;
  href: string;
  viewedAt: string;
}

const STORAGE_KEY = "rheumhub_patient_recently_viewed";
const MAX_ITEMS = 6;

function readItems(): RecentItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function recordRecentView(item: Omit<RecentItem, "viewedAt">): void {
  const existing = readItems().filter(
    i => !(i.type === item.type && i.id === item.id)
  );
  const updated: RecentItem[] = [
    { ...item, viewedAt: new Date().toISOString() },
    ...existing,
  ].slice(0, MAX_ITEMS);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent("rheumhub:recently-viewed-updated"));
  } catch { /* storage full — ignore */ }
}

export function clearRecentlyViewed(): void {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent("rheumhub:recently-viewed-updated"));
}

export function useRecentlyViewed() {
  const [items, setItems] = useState<RecentItem[]>(readItems);

  useEffect(() => {
    const handler = () => setItems(readItems());
    window.addEventListener("rheumhub:recently-viewed-updated", handler);
    window.addEventListener("focus", handler);
    return () => {
      window.removeEventListener("rheumhub:recently-viewed-updated", handler);
      window.removeEventListener("focus", handler);
    };
  }, []);

  const refresh = useCallback(() => setItems(readItems()), []);

  return { items, refresh };
}
